import { Row } from "@/dom/components/Panel/Row";
import { Section } from "@/dom/components/Panel/Section";
import { ToggleButtons } from "@/dom/components/ToggleButtons/ToggleButtons";
import { Dimensions, Furniture } from "@/state/Furniture";
import { isNotEmpty } from "@/utils/typeGuards";
import _ from "lodash";
import React, { useMemo } from "react";

type DimensionsSectionProps = {
    dimensions: Dimensions;
    otherVariants: ReadonlyArray<Furniture>;
    onOtherVariantSelected: (otherVariantId: string) => void;
};

export const DimensionsSection: React.FC<DimensionsSectionProps> = ({
    dimensions,
    otherVariants,
    onOtherVariantSelected,
}) => {
    const { widthOptions, heightOptions, depthOptions } = useMemo(() => {
        const widthOptions = getDimensionOptions("width", dimensions, otherVariants);
        const heightOptions = getDimensionOptions("height", dimensions, otherVariants);
        const depthOptions = getDimensionOptions("depth", dimensions, otherVariants);

        return { widthOptions, heightOptions, depthOptions };
    }, [dimensions, otherVariants]);

    return (
        <Section title="Размеры">
            <DimensionRow
                label="Длина"
                options={depthOptions}
                unit={dimensions.unit}
                onSelect={onOtherVariantSelected}
            />
            <DimensionRow
                label="Ширина"
                options={widthOptions}
                unit={dimensions.unit}
                onSelect={onOtherVariantSelected}
            />
            <DimensionRow
                label="Высота"
                options={heightOptions}
                unit={dimensions.unit}
                onSelect={onOtherVariantSelected}
            />
        </Section>
    );
};

type DimensionRowProps = {
    label: string;
    options: DimensionOption[];
    onSelect: (otherVariantId: string) => void;
    unit: string;
};

const DimensionRow: React.FC<DimensionRowProps> = ({ label, options, onSelect, unit }) => {
    return (
        <Row>
            <span>
                {label} ({unit})
            </span>
            {options.length === 1 ? (
                <span>{options[0].label}</span>
            ) : (
                <ToggleButtons
                    options={options}
                    onChange={(variantId) => {
                        if (isNotEmpty(variantId)) {
                            onSelect(variantId);
                        }
                    }}
                    value={""}
                />
            )}
        </Row>
    );
};

type DimensionOption = {
    label: string;
    value: string;
};

const getDimensionOptions = (
    dimension: "width" | "height" | "depth",
    dimensions: Dimensions,
    otherVariants: ReadonlyArray<Furniture>
) => {
    const options: DimensionOption[] = [{ label: dimensions[dimension].toString(), value: "" }];

    for (const variant of otherVariants) {
        const otherVariantDimension = variant.data.dimensions[dimension];
        if (otherVariantDimension !== dimensions[dimension]) {
            options.push({ label: otherVariantDimension.toString(), value: variant.id });
        }
    }

    return _.orderBy(options, ["label"], ["asc"]);
};
